import { useCallback, useContext, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { SEND_ANTRAG_MESSAGE_MUTATION, UPLOAD_ANTRAG_FILE_MUTATION } from 'Queries/Mutations';
import { GET_MY_TASKS_QUERY, GET_TASK_DETAIL_QUERY } from 'Queries/Queries';
import { ErrorHandlingContext } from 'context/ErrorHandling';

import { mapInstallerAntragDetail } from 'components/MyTaskArea/utils/mapTaskDetail';

import { OPEN_TASK_STATUSES, TASK_STATUS } from 'global/constants';

export const useMyTasks = ({ skip = false } = {}) => {
  const { setShowError } = useContext(ErrorHandlingContext);
  const [hasError, setHasError] = useState(false);

  const { data, loading } = useQuery(GET_MY_TASKS_QUERY, {
    skip,
    onError: (error) => {
      console.error('useMyTasks error:', error);
      setHasError(true);
      setShowError((prev) => !prev && true); // Prevent multiple triggers
    },
  });

  const tasks = useMemo(() => {
    const list = data?.getInstallerAntraege ?? null;
    if (!list) return null;
    return [...list].sort((a, b) => new Date(b.eingangsdatum) - new Date(a.eingangsdatum));
  }, [data]);

  const openTaskCount = loading
    ? undefined
    : (tasks ?? []).filter((t) => OPEN_TASK_STATUSES.includes(t.antragstatus)).length;

  const newTaskCount = loading
    ? undefined
    : (tasks ?? []).filter((t) => t.antragstatus === TASK_STATUS.NEW).length;

  return { tasks, isLoading: loading, hasError, openTaskCount, newTaskCount };
};

export const useTaskDetail = ({ taskId, skip = false } = {}) => {
  const { setShowError } = useContext(ErrorHandlingContext);
  const { data, loading } = useQuery(GET_TASK_DETAIL_QUERY, {
    variables: { antragId: taskId },
    skip: !taskId || skip,
    onError: (error) => {
      console.error('useTaskDetail error:', error);
      setShowError((prev) => !prev && true); // Prevent multiple triggers
    },
  });

  const taskDetail = useMemo(
    () => mapInstallerAntragDetail(data?.getInstallerAntragDetail),
    [data]
  );

  return { taskDetail, isLoading: loading };
};

/**
 * Returns a `submitAntragMessage` function that:
 * 1. Uploads every requested file via `uploadAntragFile` and collects the returned file IDs.
 * 2. Sends the combined message (text + field corrections + uploaded file IDs) via `sendAntragMessage`.
 *
 * @param {number} antragId - ID of the antrag to reply to
 * @returns {object} { submitAntragMessage, isSubmitting, submitError, submitSuccess }
 */
export const useSendAntragMessage = (antragId) => {
  const [uploadAntragFile] = useMutation(UPLOAD_ANTRAG_FILE_MUTATION);
  const [sendAntragMessage] = useMutation(SEND_ANTRAG_MESSAGE_MUTATION);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(/** @type {string | null} */ (null));
  const [submitSuccess, setSubmitSuccess] = useState(false);

  /**
   * @param {object} params
   * @param {string} params.message - free-text comment ("Weitere Anmerkungen")
   * @param {Array<{entityId: number, field: string, value: string}>} params.fields - corrected installer fields
   * @param {Array<{documentType: string, file: {base64: string, contentType: string, name: string}}>} params.filesToUpload
   *   Each entry maps one `documentType` to one file object (data-URI base64).
   */
  const submitAntragMessage = useCallback(
    async ({ message, fields, filesToUpload }) => {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      try {
        // 1. Upload each file and collect { documentType, fileId } pairs
        const fileRefs = await Promise.all(
          filesToUpload.map(
            async (/** @type {{ documentType: string, file: any }} */ { documentType, file }) => {
              const base64 = file.base64.includes(',') ? file.base64.split(',')[1] : file.base64;
              const result = await uploadAntragFile({
                variables: {
                  antragId,
                  file: {
                    base64,
                    contentType: file.contentType || null,
                    fileName: file.name || 'document',
                  },
                },
              });

              const output = result?.data?.uploadAntragFile;
              if (!output?.id) {
                throw new Error('Datei-Upload fehlgeschlagen.');
              }

              return { documentType, fileId: output.id };
            }
          )
        );

        // 2. Build files array from uploaded file IDs
        const files = fileRefs
          .filter((ref) => ref.fileId != null)
          .map((ref) => ({ documentType: ref.documentType, fileId: ref.fileId }));

        // 3. Send the message
        const result = await sendAntragMessage({
          variables: {
            antragId,
            input: {
              message: message || '',
              fields,
              files,
            },
          },
        });

        if (!result?.data?.sendAntragMessage) {
          throw new Error('Nachricht konnte nicht gesendet werden. Bitte versuche es erneut.');
        }

        setSubmitSuccess(true);
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [antragId, uploadAntragFile, sendAntragMessage]
  );

  return { submitAntragMessage, isSubmitting, submitError, submitSuccess };
};
